import React, { ElementType, FC, useState } from 'react';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import { FloatingActionBar } from '@frontend/adapto-actions';
import { useTranslation } from '@frontend/i18n';
import { useTimestampFormatter } from '@frontend/timestamp-formatter';
import { ComponentProps } from '@frontend/types';
import { theme } from '@frontend/theme';
import {
  IconButton,
  ListRow,
  ListRowProps,
  MoreIcon,
  PopoverMenu,
  PopoverMenuItem,
  PopoverMenuItemDropdown,
  usePopoverMenu,
  Text,
  IconProps,
  FontColorType,
  FontWeight,
} from '@frontend/design-system';

dayjs.extend(calendar);

export type StatusAlert = {
  text: string;
  color?: FontColorType;
  weight?: FontWeight;
  customColor?: string;
};

export type VariantRowProps<C extends React.ElementType = 'li'> = ListRowProps<C> & {
  Lead: JSX.Element;
  Title: JSX.Element;
  Content?: JSX.Element;
  hideTodayLabel?: boolean;
  timestamp?: string;
  timestampOverride?: string;
  isExpanded?: boolean;
  ExpandedContent?: JSX.Element;
  trackingId?: string;
  actions?: {
    Icon?: PopoverMenuItem['Icon'];
    label: string;
    action: () => void;
    subMenuItems?: React.ComponentProps<typeof PopoverMenuItemDropdown>['subMenuItems'];
  }[];
  iconButtonProps?: VariantIconButtonProps['iconButtonProps'];
  TitlePrefix?: JSX.Element;
  TitlePostfix?: JSX.Element;
  timestampOverrideProps?: StatusAlert | null;
  hideInlineActions?: boolean;
};

export const ActionableListRow = <C extends React.ElementType = 'li'>({
  Lead,
  Title,
  Content,
  isExpanded,
  ExpandedContent,
  hideTodayLabel,
  timestamp,
  timestampOverride,
  timestampOverrideProps,
  actions,
  as,
  iconButtonProps,
  trackingId,
  TitlePrefix,
  TitlePostfix,
  hideInlineActions,
  ...rest
}: VariantRowProps<C>) => {
  const [isHovering, setIsHovering] = useState(false);

  const { getTriggerProps, getMenuProps, getItemProps, isOpen, close } = usePopoverMenu({
    placement: 'bottom-end',
    middlewareOptions: {
      offset: {
        alignmentAxis: 0,
        mainAxis: 0,
      },
    },
  });
  const formatTimestamp = useTimestampFormatter();

  return (
    <ListRow
      trackingId={trackingId}
      // Something is weird here with TS
      as={as as ElementType}
      css={{ gridTemplateColumns: 'auto 1fr' }}
      onMouseEnter={() => {
        setIsHovering(true);
      }}
      onMouseLeave={() => {
        if (!isOpen) {
          setIsHovering(false);
        }
      }}
      {...rest}
    >
      {Lead}
      <ListRow.Content css={{ display: 'grid', gridTemplateRows: 'repeat(auto-fit, minmax(24px, auto))' }}>
        {TitlePrefix}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 0 }}>
          <div css={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            {Title}
            {TitlePostfix}
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {!hideInlineActions && (
              <div
                style={{
                  position: 'absolute',
                  right: theme.spacing(1.5),
                  width: '60px',
                  display: 'flex',
                  justifyContent: 'end',
                  alignItems: 'center',
                  visibility: isHovering ? 'visible' : 'hidden',
                  opacity: isHovering ? 1 : 0,
                  transition: 'all 200ms ease-in-out',
                }}
              >
                {iconButtonProps && <VariantIconButton iconButtonProps={iconButtonProps} />}
                {actions && (
                  <VariantMenu
                    actions={actions}
                    getMenuProps={getMenuProps}
                    getItemProps={getItemProps}
                    getTriggerProps={getTriggerProps}
                    closeMenu={close}
                  />
                )}
              </div>
            )}
            <Text
              size='small'
              color={timestampOverrideProps ? timestampOverrideProps.color : 'light'}
              weight={timestampOverrideProps ? timestampOverrideProps.weight : 'regular'}
              css={{
                flexShrink: 0,
                width: 'max-content',
                visibility: isHovering ? 'hidden' : 'visible',
                opacity: isHovering ? 0 : 1,
                transition: 'all 200ms ease-in-out',
                color: timestampOverrideProps ? timestampOverrideProps.customColor : '',
              }}
            >
              {timestampOverride || formatTimestamp(timestamp, hideTodayLabel)}
            </Text>
          </div>
        </div>
        {Content}
        {isExpanded && ExpandedContent}
      </ListRow.Content>
    </ListRow>
  );
};

export const FloatingActionableListRow = <C extends React.ElementType = 'li'>(props: VariantRowProps<C>) => {
  const { iconButtonProps, actions } = props;
  const { getTriggerProps, getMenuProps, getItemProps, close } = usePopoverMenu({
    placement: 'bottom-end',
    middlewareOptions: {
      offset: {
        alignmentAxis: 0,
        mainAxis: 0,
      },
    },
  });
  return (
    <FloatingActionBar>
      <FloatingActionBar.Actions>
        {iconButtonProps && <VariantIconButton iconButtonProps={iconButtonProps} />}
        {actions && (
          <VariantMenu
            actions={actions}
            getMenuProps={getMenuProps}
            getItemProps={getItemProps}
            getTriggerProps={getTriggerProps}
            closeMenu={close}
          />
        )}
      </FloatingActionBar.Actions>
      <FloatingActionBar.Content>
        <ActionableListRow {...props} hideInlineActions />
      </FloatingActionBar.Content>
    </FloatingActionBar>
  );
};

type VariantMenuProps = {
  actions: {
    Icon?: PopoverMenuItem['Icon'];
    label: string;
    action: () => void;
    trackingId?: string;
    subMenuItems?: React.ComponentProps<typeof PopoverMenuItemDropdown>['subMenuItems'];
  }[];
  getItemProps: ReturnType<typeof usePopoverMenu<HTMLElement>>['getItemProps'];
  getMenuProps: ReturnType<typeof usePopoverMenu<HTMLElement>>['getMenuProps'];
  getTriggerProps: ReturnType<typeof usePopoverMenu<HTMLElement>>['getTriggerProps'];
  closeMenu?: () => void;
};

export const VariantMenu = ({ actions, getItemProps, getTriggerProps, getMenuProps, closeMenu }: VariantMenuProps) => {
  const { t } = useTranslation('base');
  const menuProps = getMenuProps();

  const triggerProps = getTriggerProps({
    onClick: (e) => {
      e.stopPropagation();
    },
  });

  return (
    <>
      <IconButton
        css={{
          height: 16,
          ':hover:not(:disabled)': { background: 'none', color: theme.colors.primary50 },
          '& svg': { width: 16, height: 16 },
        }}
        size='xxsmall'
        {...triggerProps}
        label={t('More actions')}
      >
        <MoreIcon />
      </IconButton>

      <PopoverMenu {...menuProps}>
        {actions.map(({ Icon, label, action, trackingId, subMenuItems }, index) => {
          if (subMenuItems) {
            return (
              <PopoverMenuItemDropdown
                Icon={Icon}
                {...getItemProps({ index })}
                key={label}
                trackingId={trackingId}
                subMenuItems={subMenuItems}
                closeParentMenu={closeMenu}
                showOnHover
              >
                {label}
              </PopoverMenuItemDropdown>
            );
          }

          return (
            <PopoverMenuItem
              Icon={Icon}
              {...getItemProps({
                index,
                onClick: (e) => {
                  e.stopPropagation();
                  action();
                },
              })}
              key={label}
              trackingId={trackingId}
            >
              {label}
            </PopoverMenuItem>
          );
        })}
      </PopoverMenu>
    </>
  );
};

type VariantIconButtonProps = {
  iconButtonProps: {
    label: string;
    onClick: () => void;
    Icon: FC<React.PropsWithChildren<IconProps>>;
    trackingId?: string;
    showLabelOnHover?: boolean;
    size?: ComponentProps<typeof IconButton>['size'];
    className?: string;
  };
};

const VariantIconButton = ({ iconButtonProps }: VariantIconButtonProps) => {
  const { label, onClick, Icon, trackingId, showLabelOnHover, size, className } = iconButtonProps;

  return (
    <IconButton
      className={className ?? ''}
      css={{
        height: 16,
        ':hover:not(:disabled)': { background: 'none', color: theme.colors.primary50 },
        '& svg': { width: 16, height: 16 },
      }}
      size={size ?? 'xxsmall'}
      label={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      trackingId={trackingId}
      showLabelOnHover={showLabelOnHover}
    >
      <Icon />
    </IconButton>
  );
};
